import { useState, useCallback, useEffect } from 'react'
import { kanbify } from '../lib/kanbifyClient'

/**
 * Hook for card date history — snapshots, activity events, and scheduled changes.
 * Merges card_daily_snapshots + v_activity_history for a complete timeline.
 */
export function useCardHistory(cardId) {
    const [timeline, setTimeline] = useState([])
    const [scheduledChanges, setScheduledChanges] = useState([])
    const [loading, setLoading] = useState(false)
    const [availableDates, setAvailableDates] = useState([])

    const fetchAll = useCallback(async () => {
        if (!cardId) return
        setLoading(true)

        // Unified per-card activity timeline: synthesized creation event
        // (from cards.created_by/created_at — works even for cards with no
        // card_history row), + card_history (human-readable), + comments
        // + time logs + checklist completions. Each source is best-effort
        // (a failing one must not blank the whole timeline).
        const safe = async (p) => { try { return (await p)?.data || [] } catch { return [] } }
        const [cardRows, history, scheduledRes, comments, timeLogs, checklists, attachments] = await Promise.all([
            safe(kanbify.from('cards')
                .select('id, created_at, created_by, completion_payload, profiles:created_by ( id, full_name, avatar_url )')
                .eq('id', cardId).limit(1)),
            safe(kanbify.from('card_history')
                .select('*, profiles:user_id ( id, full_name, avatar_url )')
                .eq('card_id', cardId).order('created_at', { ascending: false }).limit(200)),
            kanbify.from('card_scheduled_changes')
                .select('*, profiles:created_by ( id, full_name )')
                .eq('card_id', cardId).order('effective_date', { ascending: true }),
            safe(kanbify.from('comments')
                .select('id, text, created_at, profiles:user_id ( full_name, avatar_url )')
                .eq('card_id', cardId).order('created_at', { ascending: false }).limit(100)),
            safe(kanbify.from('time_logs')
                .select('id, duration_minutes, description, created_at, profiles:user_id ( full_name, avatar_url )')
                .eq('card_id', cardId)
                .gt('duration_minutes', 0)
                .order('created_at', { ascending: false }).limit(100)),
            // Checklist items — pull `created_at` too, so the history can
            // show "added item «X»" in addition to "ticked item «X»".
            // (no `added_by` column on checklist_items — leave actor blank.)
            safe(kanbify.from('checklists')
                .select('id, checklist_items ( id, text, created_at, completed_at, profiles:completed_by ( full_name, avatar_url ) )')
                .eq('card_id', cardId)),
            // Attachments — file upload events.
            safe(kanbify.from('attachments')
                .select('id, name, created_at, profiles:created_by ( full_name, avatar_url )')
                .eq('card_id', cardId).order('created_at', { ascending: false }).limit(100)),
        ])

        // Resolve list ids seen in history → names for "moved to «list»".
        const listIds = new Set()
        for (const h of history) {
            for (const s of [h.from_state, h.to_state]) {
                if (s && typeof s === 'object' && s.list_id) listIds.add(s.list_id)
            }
        }
        const listName = new Map()
        if (listIds.size) {
            const ls = await safe(kanbify.from('lists').select('id, name').in('id', [...listIds]))
            ls.forEach((l) => listName.set(l.id, l.name))
        }

        const PRIO = { highest: 'Urgent', high: 'Ridicată', medium: 'Medie', low: 'Scăzută', lowest: 'Minimă' }
        const fmtD = (v) => {
            if (!v) return '—'
            const d = new Date(v)
            return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ro-RO')
        }
        const describe = (h) => {
            const f = (h.from_state && typeof h.from_state === 'object') ? h.from_state : {}
            const t = (h.to_state && typeof h.to_state === 'object') ? h.to_state : {}
            switch (h.action) {
                case 'created': case 'INSERT': return 'Card creat'
                case 'completed': return 'Marcat finalizat'
                case 'reopened': return 'Redeschis'
                case 'deleted': case 'DELETE': return 'Card șters'
                case 'claimed': return 'Preluat din pool'
                case 'unclaimed': return 'Repus în pool'
                case 'resumed': return 'Reluat'
                case 'paused_by_priority': return 'Pus pe pauză (procedură prioritară)'
                case 'moved_list': return `Mutat în lista «${listName.get(t.list_id) || '?'}»`
                default: break
            }
            const parts = []
            if ((f.title || t.title) && f.title !== t.title) parts.push(`Titlu: «${f.title || '—'}» → «${t.title || '—'}»`)
            if (String(f.list_id) !== String(t.list_id) && (f.list_id || t.list_id)) parts.push(`Mutat în «${listName.get(t.list_id) || '?'}»`)
            if (String(f.due_date) !== String(t.due_date)) parts.push(`Termen: ${fmtD(f.due_date)} → ${fmtD(t.due_date)}`)
            if ((f.priority || t.priority) && f.priority !== t.priority) parts.push(`Prioritate: ${PRIO[f.priority] || f.priority || '—'} → ${PRIO[t.priority] || t.priority || '—'}`)
            if (f.is_completed !== t.is_completed) parts.push(t.is_completed ? 'Marcat finalizat' : 'Redeschis')
            if (f.description !== t.description) parts.push('Descriere actualizată')
            return parts.length ? parts.join(' · ') : 'Modificat'
        }
        const mk = (id, ts, name, avatar, text) => ({
            id, type: 'snapshot',
            date: ts ? String(ts).split('T')[0] : null,
            timestamp: ts, user_name: name || null, avatar_url: avatar || null,
            changed_fields: [], detail_text: text,
        })

        const items = []
        const card = cardRows[0]
        if (card?.created_at) {
            items.push(mk(`created-${cardId}`, card.created_at,
                card.profiles?.full_name, card.profiles?.avatar_url,
                `Creat de ${card.profiles?.full_name || 'cineva'}`))
        }
        for (const h of history) {
            // skip a redundant 'created' history row (synthesized above)
            if (h.action === 'created' || h.action === 'INSERT') continue
            items.push(mk(h.id, h.created_at, h.profiles?.full_name, h.profiles?.avatar_url, describe(h)))
        }
        for (const c of comments) {
            items.push(mk(`cm-${c.id}`, c.created_at, c.profiles?.full_name, c.profiles?.avatar_url,
                `Comentariu: „${String(c.text || '').slice(0, 80)}${(c.text || '').length > 80 ? '…' : ''}”`))
        }
        for (const tl of timeLogs) {
            items.push(mk(`tl-${tl.id}`, tl.created_at, tl.profiles?.full_name, tl.profiles?.avatar_url,
                `${tl.duration_minutes || 0} min înregistrate${tl.description ? ' · ' + tl.description : ''}`))
        }
        for (const cl of (checklists || [])) {
            for (const it of (cl.checklist_items || [])) {
                // Item-added event (creation time, distinct from bifare).
                if (it.created_at) {
                    items.push(mk(`ci-add-${it.id}`, it.created_at, null, null,
                        `Adăugat în checklist: „${String(it.text || '').slice(0, 60)}”`))
                }
                if (!it.completed_at) continue
                items.push(mk(`ci-${it.id}`, it.completed_at, it.profiles?.full_name, it.profiles?.avatar_url,
                    `Bifat: „${String(it.text || '').slice(0, 60)}”`))
            }
        }
        // Attachments — file uploads.
        for (const att of (attachments || [])) {
            items.push(mk(`at-${att.id}`, att.created_at, att.profiles?.full_name, att.profiles?.avatar_url,
                `Atașat fișier: „${String(att.name || '').slice(0, 60)}”`))
        }

        // KPI completions — one event per (user, submitted_at) in
        // completion_payload.by_user. Names come from a separate profiles
        // fetch since the JSON has only ids.
        const payload = card?.completion_payload
        if (payload && typeof payload === 'object' && payload.by_user) {
            const kpiUids = Object.keys(payload.by_user)
            const kpiNames = new Map()
            if (kpiUids.length) {
                const profs = await safe(kanbify.from('profiles')
                    .select('id, full_name, avatar_url').in('id', kpiUids))
                for (const p of profs) kpiNames.set(p.id, p)
            }
            for (const uid of kpiUids) {
                const entry = payload.by_user[uid]
                if (!entry?.submitted_at || !entry?.values) continue
                const valStr = Object.entries(entry.values)
                    .map(([k, v]) => `${k}=${v}`).join(', ')
                const prof = kpiNames.get(uid)
                items.push(mk(`kpi-${uid}-${entry.submitted_at}`, entry.submitted_at,
                    prof?.full_name, prof?.avatar_url,
                    `Completat KPI: ${valStr}`))
            }
        }

        const merged = items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        setTimeline(merged)
        setScheduledChanges(scheduledRes.data || [])
        const dateSet = new Set(merged.map((item) => item.date).filter(Boolean))
        setAvailableDates([...dateSet].sort().reverse())
        setLoading(false)
    }, [cardId])

    useEffect(() => {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- standard data-fetch-on-mount pattern; fetchAll is memoized via useCallback
        fetchAll()
    }, [fetchAll])

    // Get timeline entries for a specific date
    const getEntriesForDate = useCallback((date) => {
        return timeline.filter((item) => item.date === date)
    }, [timeline])

    // Schedule a future change
    const scheduleChange = useCallback(async (effectiveDate, changes, userId) => {
        if (!cardId || !effectiveDate || !changes) return { error: 'Date lipsă' }

        const { data, error } = await kanbify
            .from('card_scheduled_changes')
            .insert([{
                card_id: cardId,
                effective_date: effectiveDate,
                changes,
                created_by: userId,
            }])
            .select('*, profiles:created_by ( id, full_name )')
            .single()

        if (error) return { error: error.message }

        setScheduledChanges((prev) => [...prev, data].sort((a, b) =>
            new Date(a.effective_date) - new Date(b.effective_date)
        ))
        return { data }
    }, [cardId])

    // Cancel a scheduled change
    const cancelScheduledChange = useCallback(async (changeId) => {
        const { error } = await kanbify
            .from('card_scheduled_changes')
            .update({ status: 'cancelled' })
            .eq('id', changeId)

        if (error) return { error: error.message }

        setScheduledChanges((prev) =>
            prev.map((c) => c.id === changeId ? { ...c, status: 'cancelled' } : c)
        )
        return { success: true }
    }, [])

    return {
        timeline,
        scheduledChanges,
        availableDates,
        loading,
        getEntriesForDate,
        scheduleChange,
        cancelScheduledChange,
        refetch: fetchAll,
    }
}
